import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/base/WorkCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import FilterBar, { type SortKey } from './components/FilterBar';
import { useSeo } from '@/hooks/useSeo';
import { works, workYears } from '@/mocks/works';

const PAGE_SIZE = 6;

export default function Category() {
  const [params, setParams] = useSearchParams();

  const type = params.get('type') ?? '全部';
  const year = params.get('year') ?? '全部';
  const genre = params.get('genre') ?? '全部';
  const sort = (params.get('sort') as SortKey) ?? 'latest';
  const page = Math.max(1, Number(params.get('page') ?? '1'));

  useSeo({
    title: '影视作品分类 - 夏目彩春',
    description:
      '按类型、年份与题材分类浏览夏目彩春参演的电影与电视剧作品，包含作品封面、类型年份标签与剧情简介，快速找到你想看的作品。',
    keywords: '夏目彩春作品,影视作品分类,电影列表,电视剧列表,作品筛选',
  });

  const filtered = useMemo(() => {
    const list = works.filter((w) => {
      const matchType = type === '全部' || w.type === type;
      const matchYear = year === '全部' || String(w.year) === year;
      const matchGenre = genre === '全部' || w.genre.includes(genre);
      return matchType && matchYear && matchGenre;
    });
    return list.sort((a, b) => (sort === 'hot' ? b.rating - a.rating : b.year - a.year));
  }, [type, year, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === '全部' || (key === 'sort' && value === 'latest')) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    next.delete('page');
    setParams(next);
  };

  const resetFilters = () => setParams(new URLSearchParams());

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-background-50">
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-8 md:py-12">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视作品' }]} />

        <div className="mt-5 mb-8">
          <span className="font-label text-xs tracking-[0.25em] uppercase text-accent-600">Works Collection</span>
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950 mt-2">影视作品分类</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl">
            这里汇集了夏目彩春参演的电影与电视剧作品。你可以按类型、年份与题材进行筛选，并按最新或热门排序，快速找到感兴趣的内容。
          </p>
        </div>

        <FilterBar
          type={type}
          year={year}
          genre={genre}
          sort={sort}
          years={workYears}
          onType={(v) => updateParam('type', v)}
          onYear={(v) => updateParam('year', v)}
          onGenre={(v) => updateParam('genre', v)}
          onSort={(v) => updateParam('sort', v)}
          onReset={resetFilters}
        />

        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-foreground-500">
            共找到 <span className="font-label text-primary-600">{filtered.length}</span> 部作品
          </p>
        </div>

        {pageItems.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 md:gap-6">
              {pageItems.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到符合筛选条件的作品"
            description="试着调整或重置筛选条件，浏览更多夏目彩春的影视作品。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}